import React, { useState, useCallback } from 'react';
import { IdpTypeStep } from './IdpTypeStep';
import { CredentialsStep } from './CredentialsStep';
import { AttributeMappingsStep, AttributeMapping } from './AttributeMappingsStep';
import { ConfirmationStep } from './ConfirmationStep';
import { validateIdpForm, IdpFormErrors } from './idp-validation';
import { IDP_PRESETS, resolveMetadataUrl, type IdpProvider, type IdpPreset } from './idp-presets';
import * as api from '../../services/api-client';
import { isClientError } from '../../services/api-client';
import { StepIndicator } from '../ui/step-indicator';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';

export interface IdpConfigWizardProps {
  readonly onComplete: () => void;
}

interface IdpFormState {
  provider: IdpProvider;
  type: 'oidc' | 'saml';
  templateValue: string;
  metadataUrl: string;
  clientId: string;
  clientSecret: string;
  adminGroupId: string;
  attributeMappings: AttributeMapping[];
}

const STEPS = [
  'Choose Provider',
  'Credentials',
  'Attribute Mappings',
  'Review & Confirm',
] as const;

function getPreset(provider: IdpProvider): IdpPreset {
  return IDP_PRESETS.find((p) => p.id === provider) ?? IDP_PRESETS[3]!; // fallback to custom-oidc
}

export function IdpConfigWizard({ onComplete }: IdpConfigWizardProps): React.ReactElement {
  const [currentStep, setCurrentStep] = useState(0);
  const [formState, setFormState] = useState<IdpFormState>(() => {
    const defaultPreset = getPreset('entra-id');
    return {
      provider: 'entra-id',
      type: defaultPreset.protocol,
      templateValue: '',
      metadataUrl: '',
      clientId: '',
      clientSecret: '',
      adminGroupId: '',
      attributeMappings: [...defaultPreset.defaultMappings],
    };
  });
  const [errors, setErrors] = useState<IdpFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const currentPreset = getPreset(formState.provider);

  // Resolve the metadata URL from preset + template value
  const resolvedMetadataUrl = currentPreset.metadataUrl
    ? currentPreset.metadataUrl
    : currentPreset.metadataUrlTemplate
      ? resolveMetadataUrl(currentPreset, formState.templateValue)
      : formState.metadataUrl;

  const handleProviderChange = useCallback((provider: IdpProvider): void => {
    const preset = getPreset(provider);
    setFormState((prev) => ({
      ...prev,
      provider,
      type: preset.protocol,
      templateValue: '',
      metadataUrl: preset.metadataUrl ?? '',
      attributeMappings: preset.defaultMappings.length > 0 ? [...preset.defaultMappings] : prev.attributeMappings,
    }));
    setErrors({});
  }, []);

  const handleFieldChange = useCallback((field: string, value: string): void => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof IdpFormErrors]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as keyof IdpFormErrors];
        return next;
      });
    }
  }, [errors]);

  const handleMappingsChange = useCallback((mappings: AttributeMapping[]): void => {
    setFormState((prev) => ({ ...prev, attributeMappings: mappings }));
  }, []);

  function validateCurrentStep(): boolean {
    if (currentStep === 1) {
      const isCustom = formState.provider === 'custom-oidc' || formState.provider === 'custom-saml';
      const hasTemplate = !!currentPreset.metadataUrlTemplate;

      // For template-based providers, validate the template value
      if (hasTemplate && !formState.templateValue.trim()) {
        setErrors({ metadataUrl: `${currentPreset.templatePlaceholderLabel} is required` } as IdpFormErrors);
        return false;
      }

      // For custom providers, validate the full form
      if (isCustom) {
        const validationErrors = validateIdpForm({
          type: formState.type,
          metadataUrl: formState.metadataUrl,
          clientId: formState.clientId,
          clientSecret: formState.clientSecret,
          attributeMappings: formState.attributeMappings,
        });
        if (Object.keys(validationErrors).length > 0) {
          setErrors(validationErrors);
          return false;
        }
      } else {
        // For known providers, just validate client ID and secret
        const fieldErrors: IdpFormErrors = {};
        if (!formState.clientId.trim()) fieldErrors.clientId = 'Client ID is required';
        if (!formState.clientSecret.trim()) fieldErrors.clientSecret = 'Client secret is required';
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          return false;
        }
      }
    }
    setErrors({});
    return true;
  }

  function handleNext(): void {
    if (!validateCurrentStep()) return;
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  }

  function handleBack(): void {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  }

  async function handleSubmit(): Promise<void> {
    setIsSubmitting(true);
    setSubmitError(null);

    const payload = {
      type: formState.type,
      metadataUrl: resolvedMetadataUrl,
      clientId: formState.clientId,
      clientSecret: formState.clientSecret,
      adminGroupId: formState.adminGroupId || undefined,
      attributeMappings: formState.attributeMappings.reduce(
        (acc, mapping) => {
          if (mapping.sourceClaim) {
            acc[mapping.sourceClaim] = mapping.targetProperty;
          }
          return acc;
        },
        {} as Record<string, string>,
      ),
    };

    try {
      await api.post('/v1/admin/idp/configure', payload);
      setSubmitSuccess(true);
      onComplete();
    } catch (error: unknown) {
      if (isClientError(error)) {
        setSubmitError(error.message);
      } else {
        setSubmitError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  function renderStepContent(): React.ReactElement {
    switch (currentStep) {
      case 0:
        return <IdpTypeStep value={formState.provider} onChange={handleProviderChange} />;
      case 1:
        return (
          <CredentialsStep
            preset={currentPreset}
            templateValue={formState.templateValue}
            metadataUrl={formState.provider === 'custom-oidc' || formState.provider === 'custom-saml' ? formState.metadataUrl : resolvedMetadataUrl}
            clientId={formState.clientId}
            clientSecret={formState.clientSecret}
            adminGroupId={formState.adminGroupId}
            errors={errors}
            onChange={handleFieldChange}
          />
        );
      case 2:
        return (
          <AttributeMappingsStep
            mappings={formState.attributeMappings}
            onChange={handleMappingsChange}
          />
        );
      case 3:
        return (
          <ConfirmationStep
            formState={{
              type: formState.type,
              metadataUrl: resolvedMetadataUrl,
              clientId: formState.clientId,
              clientSecret: formState.clientSecret,
              adminGroupId: formState.adminGroupId,
              attributeMappings: formState.attributeMappings,
            }}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return <IdpTypeStep value={formState.provider} onChange={handleProviderChange} />;
    }
  }

  return (
    <div className="space-y-4">
      {submitSuccess && (
        <Alert variant="success">
          <AlertDescription>Identity Provider configured successfully</AlertDescription>
        </Alert>
      )}

      {submitError && (
        <Alert variant="destructive">
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      <StepIndicator steps={STEPS} currentStep={currentStep} />

      <div className="mt-4">{renderStepContent()}</div>

      {currentStep < STEPS.length - 1 && (
        <div className="flex gap-2 mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0}
          >
            Back
          </Button>
          <Button type="button" onClick={handleNext}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
